import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { makeStyles } from "@mui/styles";
import { Formik, Form } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Breadcrumb from "../../common/breadcrumb";
import CommonLoader from "../../common/commonLoader";
import {
  getVendorAmendmentById,
  getVendorRegistrationById,
  getAllPaymentTerms,
  updateVendorAmendmentStatus,
} from "../../api/ap_services";
import {
  SectionCard,
  Step1CompanyDetails,
  Step2BusinessDetails,
  Step3FactoryInfo,
  Step4Statutory,
} from "../vendorRegistrationForm/steps";
import FormikTextField from "../vendorRegistrationForm/steps/FormikTextField";
import {
  initialValues,
  validationSchema,
  VENDOR_PROFILE_SUBMIT_FIELDS,
} from "../vendorProfile/vendorProfileForm";
import AmendmentHighlightStyles from "./AmendmentHighlightStyles";
import {
  mapVendorApiToFormValues,
  computeAmendmentChangedFields,
} from "./vendorAmendmentUtils";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
  },
  section: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  backRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
}));

const PROFILE_SECTIONS = [
  { title: "Company Details", key: "company" },
  { title: "Factory Information", key: "factory" },
  { title: "Business Details", key: "business" },
  { title: "Statutory Details", key: "statutory" },
];

const STATUS_OPTIONS = ["PENDING", "REJECTED", "APPROVED BY REVIEWER"];

const getCurrentUserDetails = () => {
  try {
    const userData = JSON.parse(sessionStorage.getItem("userData") || "{}");
    return {
      username: userData?.username || "ADMIN",
      groupId: Number(userData?.groupId),
    };
  } catch {
    return {
      username: "ADMIN",
      groupId: -1,
    };
  }
};

const isApVendorUser = () => {
  try {
    const userData = JSON.parse(sessionStorage.getItem("userData") || "{}");
    return Number(userData.groupId) === 0 && Number(userData.locationId) === 2;
  } catch {
    return false;
  }
};

const VendorAmendmentForm = () => {
  const { vendorAmendmentId } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [amendmentRecord, setAmendmentRecord] = useState(null);
  const [registrationRecord, setRegistrationRecord] = useState(null);
  const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("PENDING");
  const [remarks, setRemarks] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const currentUser = getCurrentUserDetails();
  const isAdminUser = currentUser.groupId === 1;

  useEffect(() => {
    if (isApVendorUser()) {
      navigate(`${process.env.PUBLIC_URL}/dashboard`, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    getAllPaymentTerms(0, 9999)
      .then((res) => {
        const list = res?.response?.responseObject ?? [];
        setPaymentTermsOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => setPaymentTermsOptions([]));
  }, []);

  const loadAmendmentData = () => {
    if (!vendorAmendmentId) {
      setAmendmentRecord(null);
      setRegistrationRecord(null);
      setLoading(false);
      return Promise.resolve();
    }
    setLoading(true);
    return getVendorAmendmentById(vendorAmendmentId)
      .then((amendRes) => {
        const amendment =
          amendRes?.response?.responseObject ?? amendRes?.response;
        if (!amendment || typeof amendment !== "object") {
          setAmendmentRecord(null);
          setRegistrationRecord(null);
          return;
        }
        setAmendmentRecord(amendment);
        const regId =
          amendment.vendorRegistrationId ?? amendment.vendor_registration_id;
        if (regId == null) {
          setRegistrationRecord(null);
          return;
        }
        return getVendorRegistrationById(regId).then((regRes) => {
          const regRaw = regRes?.response?.responseObject ?? regRes?.response;
          const regData = Array.isArray(regRaw) ? regRaw[0] : regRaw;
          setRegistrationRecord(
            regData && typeof regData === "object" ? regData : null,
          );
        });
      })
      .catch(() => {
        setAmendmentRecord(null);
        setRegistrationRecord(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isApVendorUser()) {
      setLoading(false);
      return;
    }
    loadAmendmentData();
  }, [vendorAmendmentId]);

  const amendmentFormValues = useMemo(() => {
    if (!amendmentRecord) return initialValues;
    return mapVendorApiToFormValues(amendmentRecord);
  }, [amendmentRecord]);

  const registrationFormValues = useMemo(() => {
    if (!registrationRecord) return initialValues;
    return mapVendorApiToFormValues(registrationRecord);
  }, [registrationRecord]);

  const changedFields = useMemo(() => {
    if (!amendmentRecord || !registrationRecord) return new Set();
    return computeAmendmentChangedFields(
      registrationFormValues,
      amendmentFormValues,
    );
  }, [
    amendmentRecord,
    registrationRecord,
    registrationFormValues,
    amendmentFormValues,
  ]);

  if (isApVendorUser()) {
    return null;
  }

  if (loading) {
    return (
      <Fragment>
        <CommonLoader />
        <Breadcrumb title="Vendor Amendment Review" />
      </Fragment>
    );
  }

  if (!amendmentRecord) {
    return (
      <Fragment>
        <Breadcrumb title="Vendor Amendment Review" />
        <Container fluid>
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6" color="textSecondary" align="center" sx={{ py: 4 }}>
                Vendor amendment not found.
              </Typography>
              <Box textAlign="center">
                <Button
                  variant="outlined"
                  onClick={() =>
                    navigate(`${process.env.PUBLIC_URL}/master/vendorAmendmentList`)
                  }
                >
                  Back to list
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Fragment>
    );
  }

  const status =
    amendmentRecord.amendmentStatus ??
    amendmentRecord.amendment_status ??
    "PENDING";
  const companyName =
    amendmentRecord.companyName ?? amendmentRecord.company_name ?? "";
  const amendmentNo =
    amendmentRecord.amendmentNo ??
    amendmentRecord.amendment_no ??
    "";
  const availableStatusOptions = isAdminUser
    ? [...STATUS_OPTIONS, "COMPLETED"]
    : STATUS_OPTIONS;
  const existingReviewerRemarks =
    amendmentRecord.reviewerRemarks ?? amendmentRecord.reviewer_remarks ?? "";
  const existingFinalRemarks =
    amendmentRecord.finalApprovalRemarks ??
    amendmentRecord.final_approval_remarks ??
    "";
  const reviewedBy =
    amendmentRecord.reviewedBy ?? amendmentRecord.reviewed_by ?? "";
  const reviewedDateRaw =
    amendmentRecord.reviewedDate ?? amendmentRecord.reviewed_date ?? "";
  const reviewerRemarks =
    amendmentRecord.reviewerRemarks ?? amendmentRecord.reviewer_remarks ?? "";
  const finalApprovalBy =
    amendmentRecord.finalApprovalBy ?? amendmentRecord.final_approval_by ?? "";
  const finalApprovalDateRaw =
    amendmentRecord.finalApprovalDate ??
    amendmentRecord.final_approval_date ??
    "";
  const finalApprovalRemarks =
    amendmentRecord.finalApprovalRemarks ??
    amendmentRecord.final_approval_remarks ??
    "";

  const formatDateTime = (value) => {
    if (!value) return "";
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("DD-MM-YYYY") : String(value);
  };

  const reviewedDate = formatDateTime(reviewedDateRaw);
  const finalApprovalDate = formatDateTime(finalApprovalDateRaw);

  const openStatusDialog = () => {
    setSelectedStatus(status);
    setRemarks(status === "COMPLETED" ? existingFinalRemarks : existingReviewerRemarks);
    setStatusDialogOpen(true);
  };

  const closeStatusDialog = () => {
    if (statusSaving) return;
    setStatusDialogOpen(false);
  };

  const handleSubmitStatus = () => {
    const payload = {
      vendorAmendmentId: Number(vendorAmendmentId),
      vendorRegistrationId:
        amendmentRecord?.vendorRegistrationId ??
        amendmentRecord?.vendor_registration_id,
      amendmentStatus: selectedStatus,
    };
    if (selectedStatus === "COMPLETED") {
      payload.finalApprovalRemarks = remarks;
    } else {
      payload.reviewerRemarks = remarks;
    }
    setStatusSaving(true);
    updateVendorAmendmentStatus(payload)
      .then((res) => {
        const responseCode = res?.response?.responseCode;
        if (responseCode === true || responseCode === "true") {
          toast.success("Amendment status updated");
          setStatusDialogOpen(false);
          loadAmendmentData();
          return;
        }
        toast.error(res?.response?.message || "Failed to update amendment status");
      })
      .catch(() => toast.error("Failed to update amendment status"))
      .finally(() => setStatusSaving(false));
  };

  const getStatusColor = (status) => {
  switch (status?.toUpperCase()) {
    case "PENDING":
      return {
        bg: "#ff9800", // orange
        hover: "#f57c00",
      };

    case "REJECTED":
      return {
        bg: "#f44336", // red
        hover: "#d32f2f",
      };

    case "APPROVED BY REVIEWER":
      return {
        bg: "#2196f3", // blue
        hover: "#1976d2",
      };

    case "COMPLETED":
      return {
        bg: "#4caf50", // green
        hover: "#388e3c",
      };

    default:
      return {
        bg: "#9e9e9e", // grey
        hover: "#757575",
      };
  }
};

  return (
    <Fragment>
      <Breadcrumb title="Vendor Amendment Review" />
      <Container fluid>
        <Card className={classes.card}>
          <CardContent>
            <Box className={classes.backRow}>
              <Box>
                <Typography variant="h6">{companyName}</Typography>
                {amendmentNo ? (
                  <Typography variant="body2" color="textSecondary">
                    Amendment No: {amendmentNo}
                  </Typography>
                ) : null}
                {/* <Typography variant="body2" color="textSecondary">
                  Amendment ID: {vendorAmendmentId}
                  {registrationRecord?.vendorRegistrationId != null && (
                    <>
                      {" "}
                      | Registration ID:{" "}
                      {registrationRecord.vendorRegistrationId ??
                        registrationRecord.id}
                    </>
                  )}
                </Typography> */}
              </Box>
              <Box display="flex" gap={1} alignItems="center">
                <Typography variant="body2" color="textSecondary">
                  AMENDMENT STATUS:
                </Typography>
                <Button
                  variant="contained"
                  // color="warning"
                  size="small"
                  // sx={{
                  //   borderRadius: "16px",
                  //   textTransform: "none",
                  //   minWidth: "auto",
                  //   px: 1.5,
                  // }}
                   sx={{
                    borderRadius: "16px",
                    textTransform: "none",
                    minWidth: "auto",
                    px: 1.5,
                    backgroundColor: getStatusColor(status).bg,
                    "&:hover": {
                      backgroundColor: getStatusColor(status).hover,
                    },
                  }}
                  onClick={openStatusDialog}
                >
                  {status}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() =>
                    navigate(`${process.env.PUBLIC_URL}/master/vendorAmendmentList`)
                  }
                >
                  Back to list
                </Button>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              Highlighted fields (yellow background) differ from the current
              approved vendor registration. This view is read-only for AP team
              review.
            </Alert>

            <AmendmentHighlightStyles changedFields={changedFields} />
            <div className="vendor-amendment-review">
              <fieldset
                disabled
                style={{
                  border: 0,
                  margin: 0,
                  padding: 0,
                  minWidth: 0,
                }}
              >
                <Formik
                  key={vendorAmendmentId}
                  initialValues={amendmentFormValues}
                  validationSchema={validationSchema.pick(
                    VENDOR_PROFILE_SUBMIT_FIELDS,
                  )}
                  enableReinitialize
                  onSubmit={() => {}}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    setFieldValue,
                  }) => {
                    const renderSection = (sectionKey) => {
                      switch (sectionKey) {
                        case "company":
                          return (
                            <Step1CompanyDetails
                              formData={values}
                              onFieldChange={handleChange}
                              errors={errors}
                              touched={touched}
                              setFieldValue={setFieldValue}
                              groupId={{ isAdmin: true }}
                              isEdit
                              isVendorUser
                            />
                          );
                        case "factory":
                          return (
                            <Step3FactoryInfo
                              formData={values}
                              onFieldChange={handleChange}
                              setFieldValue={setFieldValue}
                              setFormData={(updater) => {
                                const next =
                                  typeof updater === "function"
                                    ? updater(values)
                                    : updater;
                                Object.keys(next).forEach((key) =>
                                  setFieldValue(key, next[key]),
                                );
                              }}
                              errors={errors}
                              touched={touched}
                            />
                          );
                        case "business":
                          return (
                            <Step2BusinessDetails
                              formData={values}
                              onFieldChange={handleChange}
                              errors={errors}
                              touched={touched}
                              setFieldValue={setFieldValue}
                            />
                          );
                        case "statutory":
                          return (
                            <Step4Statutory
                              formData={values}
                              setFieldValue={setFieldValue}
                              errors={errors}
                              touched={touched}
                              paymentTermsOptions={paymentTermsOptions}
                              isVendorUser
                            />
                          );
                        default:
                          return null;
                      }
                    };

                    return (
                      <Form>
                        {PROFILE_SECTIONS.map((section, index) => (
                          <Box key={section.key} className={classes.section}>
                            {index > 0 && <Divider sx={{ mb: 3 }} />}
                            <Typography
                              variant="h6"
                              className={classes.sectionTitle}
                            >
                              {section.title}
                            </Typography>
                            {renderSection(section.key)}
                          </Box>
                        ))}

                          <SectionCard title="OVERALL COMMENTS">
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <FormikTextField
                                multiline
                                rows={4}
                                label="Overall Comments and General Observations"
                                name="overallComments"
                              />
                            </Grid>
                          </Grid>
                          </SectionCard>
                        {/* </Box> */}

                        {status !== "PENDING" && (
                            <SectionCard title="AMENDMENT STATUS DETAILS">
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="REVIEWED_BY"
                                  value={reviewedBy}
                                  InputProps={{ readOnly: true }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="REVIEWED_DATE"
                                  value={reviewedDate}
                                  InputProps={{ readOnly: true }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  multiline
                                  minRows={2}
                                  label="REVIEWER_REMARKS"
                                  value={reviewerRemarks}
                                  InputProps={{ readOnly: true }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="FINAL_APPROVAL_BY"
                                  value={finalApprovalBy}
                                  InputProps={{ readOnly: true }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="FINAL_APPROVAL_DATE"
                                  value={finalApprovalDate}
                                  InputProps={{ readOnly: true }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  multiline
                                  minRows={2}
                                  label="FINAL_APPROVAL_REMARKS"
                                  value={finalApprovalRemarks}
                                  InputProps={{ readOnly: true }}
                                />
                              </Grid>
                            </Grid>
                          {/* </Box> */}
                          </SectionCard>
                        )}
                      </Form>
                    );
                  }}
                </Formik>
              </fieldset>
            </div>
          </CardContent>
        </Card>
      </Container>
      <Dialog
        open={statusDialogOpen}
        onClose={closeStatusDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Update Amendment Status</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              select
              label="Amendment Status"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              fullWidth
              size="small"
            >
              {availableStatusOptions.map((statusOption) => (
                <MenuItem key={statusOption} value={statusOption}>
                  {statusOption}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Remarks"
              fullWidth
              multiline
              rows={4}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder={
                selectedStatus === "COMPLETED"
                  ? "Enter admin final approval remarks"
                  : "Enter reviewer remarks"
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStatusDialog} disabled={statusSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitStatus}
            variant="contained"
            disabled={statusSaving || !selectedStatus}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <ToastContainer autoClose={2000} />
    </Fragment>
  );
};

export default VendorAmendmentForm;
